import { memo, useCallback, useEffect, useMemo, useState } from "react";
import useUserStore from "../../store/userStore";
import { useFirebase } from "../firebase";
import {
  useCustomToast,
  Skeleton,
  Tooltip,
} from "../../ui_components/reusables";
import { onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { shallow } from "zustand/shallow";
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  Flex,
  Grid,
  HStack,
  Switch,
  Badge,
  Box,
  useMediaQuery,
  IconButton,
} from "@chakra-ui/react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import dayjs from "../../helpers/dayJs";
import { HiCheckCircle, HiCurrencyDollar, HiXMark } from "react-icons/hi2";
import isURL from "validator/lib/isURL";
import { StripeContext } from "./StripeContext";
import { BiDollar } from "react-icons/bi";

const PricingTable = ({
  activeSubscription,
  isSubscriptionLoading,
  products,
}) => {
  const [isMobile] = useMediaQuery("(max-width: 600px)");
  const { functions, parseError, auth } = useFirebase();
  const { customerId } = useUserStore((state) => state.user, shallow);

  const subscriptionId = activeSubscription?.id;
  const subscriptionItem = activeSubscription?.items?.data[0];
  const currentPrice = subscriptionItem?.price;

  const [currentInterval, setCurrentInterval] = useState();
  // const { Current } = useRouteManager();
  const toast = useCustomToast();

  const { mutate: redirectToPortal, isPending: isRedirectingToPortal } =
    useMutation({
      mutationFn: async ({ shouldMigrate, additionalProps }) => {
        const portalFunc = httpsCallable(functions, "getCustomerPortal");
        const migrationProps = {
          subscription_id: subscriptionId,
          subscription_item_id: subscriptionItem?.id,
          price_id: additionalProps,
          success_url: `${window.location.origin}`,
        };
        const { data } = await portalFunc({
          return_url: window.location.origin,
          customer_id: customerId,
          ...(shouldMigrate ? migrationProps : {}),
        });

        if (isURL(data)) {
          return data;
        }
      },
      onError: (ex) => {
        console.log(ex);
        toast({
          status: "error",
          title: "Something went wrong",
          description: parseError(ex).message,
        });
      },
      onSuccess: (data) => {
        window.open(data, "_self");
      },
    });

  const { mutate: redirectToCheckout, isPending: isRedirectingToCheckout } =
    useMutation({
      mutationFn: async (priceId) => {
        const checkoutFunc = httpsCallable(functions, "getCheckoutSession");
        const { data } = await checkoutFunc({
          priceId,
          success_url: `${window.location.origin}`,
          cancel_url: window.location.href,
          customerId,
        });

        if (isURL(data)) {
          return data;
        }
      },
      onError: (ex) => {
        console.log(ex);
        toast({
          status: "error",
          title: "Something went wrong",
          description: parseError(ex).message,
        });
      },
      onSuccess: (data) => {
        window.open(data, "_self");
      },
    });

  const intervals = [...new Set(products?.flatMap((p) => p.prices))]
    .reduce((acc, price) => {
      const existing = acc.find(
        (p) => p.recurring.interval === price.recurring.interval
      );
      if (!existing || price.unit_amount > existing.unit_amount)
        acc = acc
          .filter((p) => p.recurring.interval !== price.recurring.interval)
          .concat(price);
      return acc;
    }, [])
    .sort((a, b) => b.unit_amount - a.unit_amount)
    .map((p) => p.recurring.interval);

  useEffect(() => {
    if (intervals && !currentInterval) setCurrentInterval(intervals[0]);
  }, [intervals]);

  function getFeatures() {
    if (!products) return [];

    return Array.from(
      new Map(
        products
          .flatMap((p) => p.features || [])
          .map((f) => [f.id, f.entitlement_feature])
      ).values()
    );
  }

  const productHasFeature = (productId, featureId) =>
    products
      ?.find((p) => p.id === productId)
      ?.features?.some((f) => f.entitlement_feature.id === featureId) || false;

  const customerHasProduct = (productId) =>
    activeSubscription?.status === "active" &&
    activeSubscription?.items?.data?.some(
      ({ price }) => price.product === productId
    );

  /**
   *
   * @param {'portal' | 'checkout' | 'portal-migrate'} redirectTo
   * @param {boolean} isActiveProduct
   * @param {} product The product object returned from Stripe
   */
  async function redirect(redirectTo, isActiveProduct, product) {
    const priceId = getCurrentIntervalPrice(product).id;

    if (!redirectTo) {
      if (isActiveProduct) redirectTo = "portal";
      else if (!activeSubscription) redirectTo = "checkout";
      else redirectTo = "portal-migrate";
    }

    console.log("Will redirect to:", redirectTo);

    if (redirectTo === "checkout") redirectToCheckout(priceId);
    else
      redirectToPortal({
        shouldMigrate: !isActiveProduct,
        additionalProps: priceId,
      });
  }

  function swtichIntervals() {
    setCurrentInterval((prev) => {
      if (prev === intervals?.at(-1)) return intervals[0];
      else return intervals?.at(-1);
    });
  }

  function getDiscount() {
    if (!products) return;

    const data = products;
    // Step 1: Collect all unique prices and find max price per interval
    const allPrices = data.flatMap((p) => p.prices);
    // const intervals = [...new Set(allPrices.map((p) => p.recurring.interval))];
    const sortedIntervals = intervals
      .map((interval) => {
        const max = Math.max(
          ...allPrices
            .filter((p) => p.recurring.interval === interval)
            .map((p) => p.unit_amount)
        );
        return { interval, max };
      })
      .sort((a, b) => b.max - a.max)
      .map((p) => p);

    const topInterval = sortedIntervals[0];
    const bottomInterval = sortedIntervals[sortedIntervals.length - 1];

    // Step 2: For each product, find prices for top and bottom interval and calculate discount
    const discounts = data.map((product) => {
      const topPrice = product.prices.find(
        (p) => p.recurring.interval === topInterval.interval
      )?.unit_amount;
      const bottomPrice = product.prices.find(
        (p) => p.recurring.interval === bottomInterval.interval
      )?.unit_amount;

      if (!topPrice || !bottomPrice) return 0;

      const effectiveBottom = bottomPrice * 12;
      const discount = 100 - (topPrice / effectiveBottom) * 100;
      return Math.round(discount);
    });

    // Step 3: Return the highest discount
    return Math.max(...discounts);
  }

  function getCurrentIntervalPrice(product) {
    const priceObj = product?.prices?.find(
      ({ recurring }) => recurring.interval === currentInterval
    );
    const price = priceObj?.unit_amount / 100 || 0;

    const result = {
      ...priceObj,
      main: price,
      monthly:
        currentInterval === intervals[0] && price > 0
          ? (price / 12).toFixed(2)
          : price,
    };

    return result;
  }

  const remainingTrialDays =
    activeSubscription?.trial_end &&
    dayjs.unix(activeSubscription?.trial_end).diff(dayjs(), "days");

  const remainingSubscriptionDays =
    activeSubscription?.cancel_at &&
    dayjs.unix(activeSubscription?.cancel_at).diff(dayjs(), "days");

  const getSubscriptionStatus = () => {
    let isCanceled, isTrial, isTrialWithPaymentMethod, isPaused;

    isCanceled = !!activeSubscription?.cancel_at;
    isTrial = activeSubscription?.status === "trialing";
    isTrialWithPaymentMethod =
      isTrial && activeSubscription?.default_payment_method;
    isPaused = [
      "paused",
      "past-due",
      "incomplete",
      "incomplete_expired",
      "unpaid",
    ].includes(activeSubscription?.status);

    return { isCanceled, isTrial, isTrialWithPaymentMethod, isPaused };
  };

  return (
    <Flex direction="column" w="full" h="full" color="white">
      <Grid
        gridTemplateColumns={`repeat(auto-fit, minmax(200px, 1fr))`}
        pos={"sticky"}
        top="0"
        w="full"
        gridRowGap={isMobile ? "2" : "4"}
      >
        <Skeleton isLoaded={!isSubscriptionLoading && products}>
          <VStack
            pr={"5"}
            justify={"center"}
            spacing={"3"}
            align={"flex-start"}
          >
            <HStack fontSize={"xs"}>
              <Switch
                colorScheme="green"
                isChecked={currentInterval && currentInterval === intervals[0]}
                onChange={swtichIntervals}
                size={isMobile ? "md" : "sm"}
              />
              <Text
                textTransform={"capitalize"}
                fontWeight={"semibold"}
                fontSize={isMobile ? "md" : "sm"}
              >
                {currentInterval + "ly Billing"}
              </Text>
            </HStack>
            <Text fontSize={isMobile ? "xl" : "3xl"} fontWeight={"bold"}>
              Save {getDiscount()}% on yearly plans
            </Text>
          </VStack>
        </Skeleton>
        {products?.map((product) => {
          const isActive = customerHasProduct(product.id);
          const isBusy = isRedirectingToCheckout || isRedirectingToPortal;
          const status = getSubscriptionStatus();

          const warningMessage = () => {
            if (isActive) {
              if (status.isCanceled) {
                return `Your subscription will end ${
                  remainingSubscriptionDays > 1
                    ? remainingSubscriptionDays + " days"
                    : "shortly"
                }. Renew today to enjoy uninterrupted service.`;
              } else if (status.isTrial) {
                let message = `Your trial will end ${
                  remainingTrialDays > 1
                    ? remainingTrialDays + " days"
                    : "shortly"
                }. `;
                if (status.isTrialWithPaymentMethod)
                  message += ` We will activate your subscription by charging your attached payment method.`;
                else
                  message +=
                    "Please add a valid payment method to seamlessly access all services.";

                return message;
              } else if (status.isPaused) {
                return `Your subscription has been paused. Click on "Manage Subscription" to see more details.`;
              }
            }
          };

          const statusBadgeColor = () => {
            let colorScheme = "green";
            if (status.isCanceled || status.isPaused || status.isTrial)
              colorScheme = "orange";

            if (status.isTrialWithPaymentMethod) colorScheme = "green";

            return colorScheme;
          };

          return (
            <VStack
              w="full"
              key={`header_${product.id}`}
              px={!isMobile && "5"}
              spacing={"5"}
              h="full"
              justify={"space-between"}
            >
              <VStack align={"flex-start"} w="full">
                <HStack>
                  <Text fontSize={"sm"}>{product.name}</Text>
                  {isActive && (
                    <HStack spacing={"1"}>
                      <Badge
                        fontSize={"8px"}
                        colorScheme={statusBadgeColor()}
                        fontWeight={"black"}
                      >
                        {status.isTrial
                          ? `Trial`
                          : status.isPaused
                          ? "Paused"
                          : "Active"}
                      </Badge>
                    </HStack>
                  )}
                </HStack>
                <HStack align={"flex-end"} spacing={1}>
                  <Text fontSize={"3xl"} fontWeight={"black"} lineHeight={"1"}>
                    ${getCurrentIntervalPrice(product).monthly}{" "}
                  </Text>
                  <Text fontSize={"md"}>/mo</Text>
                </HStack>
                <Text fontSize={"sm"} color="gray.400">
                  {currentInterval && currentInterval === intervals[0]
                    ? `billed yearly at $${
                        getCurrentIntervalPrice(product).main
                      }`
                    : product.description || "Description goes here"}
                </Text>
              </VStack>
              <Tooltip placement="bottom" label={warningMessage()}>
                <Button
                  size="sm"
                  w="full"
                  variant={isActive ? "nav" : "solid"}
                  isLoading={isBusy}
                  onClick={() => redirect(null, isActive, product)}
                  className={
                    isActive &&
                    (status.isTrial || status.isCanceled || status.isPaused) &&
                    "has-notification"
                  }
                >
                  {isActive ? "Manage Subscription" : "Subscribe"}
                </Button>
              </Tooltip>
            </VStack>
          );
        })}
      </Grid>
      <Box
        id="green-container"
        flex="1"
        overflowY="auto"
        w="full"
        minH="0"
        pt="5"
      >
        {getFeatures().map((feature, index) => (
          <Grid
            key={`feature_${feature.id}`}
            gridTemplateColumns={
              isMobile ? "1fr auto" : `repeat(auto-fit, minmax(200px, 1fr))`
            }
            pos={"sticky"}
            top="0"
            w="full"
            bg={index % 2 == 0 ? "#212123" : "transparent"}
            borderBottom={"1px solid"}
            borderBottomColor={"gray.700"}
          >
            <HStack justify={"flex-start"} align={"center"} px="2">
              <Text fontSize={"sm"}>{feature.name}</Text>
            </HStack>
            {products?.map((product) => (
              <HStack
                key={`${feature.id}_${product.id}`}
                justify={"center"}
                align={"center"}
                px="3"
                py="2.5"
                fontSize={"20px"}
              >
                {productHasFeature(product.id, feature.id) ? (
                  <HiCheckCircle color="green" />
                ) : (
                  <HiXMark color="gray" />
                )}
              </HStack>
            ))}
          </Grid>
        ))}
      </Box>
    </Flex>
  );
};

/**
 * StripeProvider listens to Firestore at `subscription-data/{id}` for subscription changes,
 * and shares the latest subscription state across the app via context.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const StripeWrapper = ({ children }) => {
  const user = useUserStore((state) => state.user, shallow) || {};
  const setUser = useUserStore((state) => state.setUser);
  const { id, stripeAccountId } = user;
  const { auth, getDocRef, functions, getData } = useFirebase();
  const toast = useCustomToast();

  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  // const [accountMgmtModalOpen, setAccountMgmtModalOpen] = useState(false);
  // Stripe Connect state
  const [connectAccountStatus, setConnectAccountStatus] = useState();

  useEffect(() => {
    if (!id) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const docRef = getDocRef(`subscription-data/${id}`);
    let unsubscribed = false;

    if (!docRef) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (unsubscribed) return;
        setIsLoading(false);
        setSubscription(snap.exists() ? snap.data() : null);
      },
      (err) => {
        if (unsubscribed) return;
        setIsLoading(false);
        toast({
          status: "error",
          title: "Subscription Error",
          description: err.message || "Something went wrong.",
        });
        signOut(auth);
      }
    );

    // Clean up
    return () => {
      unsubscribed = true;
      unsubscribe();
    };
  }, [id]);

  useEffect(() => {
    if (!id || !stripeAccountId) {
      return;
    }

    setConnectAccountStatus({ isLoading: true });

    const docRef = getDocRef(`stripe-connect-data/${id}`);
    let unsubscribed = false;

    if (!docRef) {
      setConnectAccountStatus({ isLoading: false });
      return;
    }

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (unsubscribed) return;
        console.log("Monitoring payment profile status...");
        const accountData = snap.exists() ? snap.data() : {};
        let isComplete = true;
        if (
          !accountData?.payouts_enabled ||
          accountData?.requirements.disabled_reason !== null ||
          accountData?.requirements?.currently_due?.length > 0
        ) {
          isComplete = false;
        }
        setConnectAccountStatus({ complete: isComplete });
      },
      (err) => {
        if (unsubscribed) return;
        setConnectAccountStatus({ isLoading: false });
        toast({
          status: "error",
          title: "Connect account Error",
          description: err.message || "Something went wrong.",
        });
        signOut(auth);
      }
    );

    // Clean up
    return () => {
      unsubscribed = true;
      unsubscribe();
    };
  }, [id, stripeAccountId]);

  const customerIsPRO = useMemo(
    () =>
      subscription?.status === "active" &&
      subscription?.items?.data?.some(({ price }) => {
        const isDev = !!import.meta.env.DEV;
        const stripe_pro_product_id = isDev
          ? import.meta.env.VITE_STRIPE_PRO_PRODUCT_ID_DEV
          : import.meta.env.VITE_STRIPE_PRO_PRODUCT_ID;

        return price.product === stripe_pro_product_id;
      }),
    [subscription]
  );

  const togglePriceModal = useCallback(() => {
    setPriceModalOpen((prev) => !prev);
  }, []);

  // const toggleAccountMgmtModal = useCallback(() => {
  //   setAccountMgmtModalOpen((prev) => !prev);
  // }, []);

  const {
    data: products,
    isLoading: isProductsLoading,
    error: getProductError,
  } = useQuery({
    queryKey: ["products", id],
    enabled: !!id, // Prevents auto-run until id is truthy (not null/undefined/empty)
    queryFn: async () => {
      const productFunc = httpsCallable(functions, "getProducts");
      const result = await productFunc();

      return result.data;
    },
    onError: (ex) => {
      console.error("Error is:", ex);
      toast({
        status: "error",
        title: "Something went wrong on our side!",
        description: ex.message || String(ex),
      });
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const LockedFeature = ({ children, checkConnectAccountStatus }) => {
    // intercept clicks before they reach children
    const handleClickCapture = (e) => {
      let modalType;

      if (!customerIsPRO) modalType = "subscription";
      if (
        !modalType &&
        checkConnectAccountStatus &&
        !connectAccountStatus?.complete
      )
        modalType = "account";

      if (modalType) {
        e.preventDefault?.();
        e.stopPropagation?.();

        if (modalType === "subscription") togglePriceModal();
        // else if (modalType === "account") toggleAccountMgmtModal();
      }
    };

    // intercept keyboard activation (Enter / Space) to cover accessibility
    const handleKeyDownCapture = (e) => {
      let modalType;

      if (!customerIsPRO) modalType = "subscription";
      if (
        !modalType &&
        checkConnectAccountStatus &&
        !connectAccountStatus?.complete
      )
        modalType = "account";

      if (
        e.key === "Enter" ||
        e.key === " " ||
        e.key === "Spacebar" ||
        e.key === "Space"
      ) {
        if (modalType) {
          e.preventDefault?.();
          e.stopPropagation?.();

          if (modalType === "subscription") togglePriceModal();
          // else if (modalType === "account") toggleAccountMgmtModal();
        }
      }
    };

    const getTooltipLabel = () => {
      if (!customerIsPRO) return `This feature requires the "Pro" subscription`;
      else if (checkConnectAccountStatus && !connectAccountStatus?.complete)
        return `Your payment profile is not set up completely. Click to set up.`;
    };

    return (
      <Tooltip label={getTooltipLabel()} w="150px">
        <div
          onClickCapture={handleClickCapture}
          onKeyDownCapture={handleKeyDownCapture}
          aria-disabled={!customerIsPRO}
        >
          {children}
        </div>
      </Tooltip>
    );
  };

  const PricingModal = () => {
    return (
      <Modal
        motionPreset="slideInTop"
        size="3xl"
        isOpen={priceModalOpen}
        onClose={togglePriceModal}
        scrollBehavior="outside"
      >
        <ModalOverlay />
        <ModalContent maxH="100vh" className="gradient-border" pb="4" pr="20px">
          <ModalHeader justifyContent="flex-end">
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody>
            <PricingTable
              activeSubscription={subscription}
              isSubscriptionLoading={isLoading}
              products={products}
            />
            {/* <SubscriptionUI.PricingTable /> */}
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  };

  const ConnectAccountStatus = () => {
    const { mutateAsync: startSetup, isPending: isSetupPending } = useMutation({
      mutationFn: async () => {
        if (connectAccountStatus?.url) {
          window.location.href = connectAccountStatus.url;
          return;
        }

        const accSetupFunc = httpsCallable(functions, "createStripeAccount");
        const appUrl = window.location.href.split("?")[0];

        const result = await accSetupFunc({
          refresh_url: appUrl,
          return_url: appUrl,
        });

        return result.data;
      },
      onSuccess: (data) => {
        if (!data) return;

        setUser({ stripeAccountId: data.accountId });
        window.open(data.setupUrl, "_blank");
      },
      onError: (error) => {
        console.error("Error checking account status:", error);
        toast({
          status: "error",
          title: "Account Status Error",
          description: error.message || "Failed to check account status",
        });
      },
    });

    const { mutateAsync: getLoginLink, isPending: isLoginLinkPending } =
      useMutation({
        mutationFn: async () => {
          const loginLinkFunc = httpsCallable(
            functions,
            "createStripeLoginlink"
          );

          const appUrl = window.location.href.split("?")[0];
          const result = await loginLinkFunc({
            stripeAccountId,
            refresh_url: appUrl,
            return_url: appUrl,
          });

          return result.data;
        },
        onSuccess: (data) => {
          if (!data) return;

          // setUser({ stripeAccountId: data.accountId });
          window.open(data, "_blank");
        },
        onError: (error) => {
          console.error(
            "Error creating login link for connected account:",
            error
          );
          toast({
            status: "error",
            title: "Account Status Error",
            description: error.message || "Failed to check account status",
          });
        },
      });

    const getStatusLabel = () => {
      if (!stripeAccountId) {
        return "Click to set-up your payment profile, to start accepting payments.";
      } else if (connectAccountStatus?.complete) {
        return "Your account is receiving payments. Click to view.";
      } else
        return "Your account needs additional setup to accept payments. Click to start.";
    };

    const accountNotReady = !connectAccountStatus?.complete || !stripeAccountId;

    function handleClick() {
      if (!stripeAccountId) {
        startSetup();
      } else getLoginLink();
    }

    return (
      customerIsPRO && (
        <Tooltip
          label={getStatusLabel()}
          textAlign={"center"}
          placement="bottom"
          w="170px"
        >
          <IconButton
            variant={"nav"}
            cursor={"pointer"}
            border={accountNotReady && "1px dashed"}
            borderColor={accountNotReady && "#ffd3004d"}
            isLoading={
              isSetupPending ||
              isLoginLinkPending ||
              (stripeAccountId && !connectAccountStatus)
            }
            onClick={handleClick}
            icon={
              <Box pos="relative">
                <BiDollar size={18} />
                <Box
                  h="8px"
                  w="8px"
                  bg={accountNotReady ? "orange.400" : "green.400"}
                  borderRadius={"full"}
                  pos="absolute"
                  bottom={"-0.5"}
                  right={"-0.5"}
                />
              </Box>
            }
            borderRadius={"full"}
          />
        </Tooltip>
      )
    );

    return (
      stripeAccountId && (
        <VStack
          borderRadius={"md"}
          bg="#2a2a2c"
          // h="35px"
          alignItems={"flex-start"}
          px="1.5"
          py="0.5"
          spacing={"auto"}
          pr="3"
          userSelect={"none"}
          cursor={"pointer"}
          _hover={{
            bg: "#313134",
          }}
          // border='1px solid'
          // borderColor={'#00ff0233'} // #ffd3004d
        >
          <Text fontSize={"12px"}>Payments</Text>
          <HStack>
            <Box h="5px" w="5px" bg="green.400" borderRadius={"full"} />
            <Text fontSize={"10px"}>View profile</Text>
          </HStack>
        </VStack>
      )
    );
  };

  const values = useMemo(() => {
    return {
      subscription,
      isLoading,
      togglePriceModal,
      products,
      LockedFeature,
      // checkConnectAccountStatus,
      // toggleAccountMgmtModal,
      customerIsPRO,
      connectAccountStatus,
      ConnectAccountStatus,
      // connectAccountStatus,
      // stripeConnect,
    };
  }, [
    subscription,
    isLoading,
    products,
    connectAccountStatus,
    customerIsPRO,
    stripeAccountId,
  ]);
  return (
    <StripeContext.Provider value={values}>
      {/* <CheckCustomerProfile
        isOpen={isLoading || isProductsLoading || isAccountStatusLoading}
      /> */}
      {/* <AccountMgmtModal connectAccountStatus={connectAccountStatus} /> */}
      <PricingModal />
      {/* <AccountStatusModal isOpen={isAccountStatusLoading} />
      <AccountMgmtModal
        isOpen={accountUrl.type === "incomplete"}
        accountUrl={accountUrl}
      /> */}
      {children}
    </StripeContext.Provider>
  );
};
